/* eslint-disable @next/next/no-img-element */
"use client";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useSearchAllQuery } from "@/service/search"; // Add this line
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useAppDispatch, useAppSelector } from "@/lib/hook";
import { getFromStorage } from "@/constants/storage";
import { STORAGE_KEYS } from "@/constants/storageKeys";
import { useLazyGetProfileQuery, useLazyGetBrandsQuery } from "@/service/auth";
import { setUser, getToken } from "@/lib/slices/authSlice";
import { useViewAllCategoryQuery } from "@/service/home";
import { useGetCartQuery } from "@/service/cart";
import { useLazyGetAddressListQuery } from "@/service/address";
import { Address } from "@/types/General";
import { useLazyGetWishlistQuery } from "@/service/wishlist";
import {
  setWishlistProductIds,
  getWishlistProductIds,
} from "@/lib/slices/wishlistSlice";
import { getDeliveryMode } from "@/utils/deliveryMode";

const menuCategories = [
  {
    title: "Face",
    image: "/images/main_menu_01.png",
    items: [
      "Foundation",
      "Loose Powder",
      "Serum & Essence",
      "Face Primer",
      "Concealer",
      "Compact",
      "Base Makeup",
      "Setting Powder",
      "Hydrating Serum",
      "Makeup Primer",
      "Spot Concealer",
      "Pressed Powder",
      "Foundation",
      "Loose Powder",
      "Serum & Essence",
      "Face Primer",
      "Concealer",
    ],
  },
  {
    title: "Eyes",
    image: "/images/main_menu_02.png",
    items: [
      "Eye Liner",
      "Kajal",
      "Eye Shadow",
      "Mascara",
      "Eyebrow Enhancer",
      "False Eyelashes",
      "Eye Primer",
    ],
  },
  {
    title: "Lips",
    image: "/images/main_menu_03.png",
    items: [
      "Eye Liner",
      "Kajal",
      "Eye Shadow",
      "Mascara",
      "Eyebrow Enhancer",
      "False Eyelashes",
      "Eye Primer",
      "Eye Liner",
      "Kajal",
      "Eye Shadow",
      "Mascara",
      "Eyebrow Enhancer",
      "False Eyelashes",
      "Eye Primer",
      "Eye Liner",
    ],
  },
  {
    title: "Nails",
    image: "/images/main_menu_04.png",
    items: [
      "Eye Liner",
      "Kajal",
      "Eye Shadow",
      "Mascara",
      "Eyebrow Enhancer",
      "False Eyelashes",
      "Eye Primer",
      "Eye Liner",
      "Kajal",
      "Eye Shadow",
      "Mascara",
      "Eyebrow Enhancer",
      "False Eyelashes",
      "Eye Primer",
      "Eye Liner",
    ],
  },
  {
    title: "Tools & Brushes",
    image: "/images/main_menu_05.png",
    items: [
      "Eye Liner",
      "Kajal",
      "Eye Shadow",
      "Mascara",
      "Eyebrow Enhancer",
      "False Eyelashes",
      "Eye Primer",
    ],
  },
  {
    title: "Makeup Kits & Combos",
    image: "/images/main_menu_06.png",
    items: [
      "Eye Liner",
      "Kajal",
      "Eye Shadow",
      "Mascara",
      "Eyebrow Enhancer",
      "False Eyelashes",
      "Eye Primer",
    ],
  },
];

function Header() {
  const dispatch = useAppDispatch();

  const [getProfile] = useLazyGetProfileQuery();
  const [getWishlist] = useLazyGetWishlistQuery();
  const wishlistProductIds = useAppSelector(getWishlistProductIds);
  const authToken = useAppSelector(getToken);

  // Get pathname to check if we're on auth pages
  const pathname = usePathname();
  const isAuthPage = pathname?.startsWith("/auth/") || false;

  // Fetch categories from API - use undefined instead of {} to avoid infinite re-renders
  // Skip on auth pages to avoid unnecessary API calls
  const { data: categoriesData } = useViewAllCategoryQuery(undefined, {
    skip: isAuthPage,
  });
  const categories = categoriesData?.data || [];

  // Fetch address list for delivery location
  const [getAddressList, { data: addressResponse }] = useLazyGetAddressListQuery();
  const addresses = addressResponse?.data || [];
  const firstAddress = addresses.length > 0 ? addresses[0] : null;

  // Fetch brands from API - dynamically fetch all brands
  const [getBrands, { data: brandsData, isLoading: brandsLoading }] = useLazyGetBrandsQuery();
  const [allBrands, setAllBrands] = React.useState<any[]>([]);

  // Fetch all brands dynamically
  React.useEffect(() => {
    const fetchAllBrands = async () => {
      try {
        // First, fetch with a small limit to get total count
        const firstPageResult = await getBrands({ page: 1, limit: 10 }).unwrap();
        const totalCount = firstPageResult?.data?.data_count || 0;

        if (totalCount > 0) {
          // Then fetch all brands using the total count as limit
          const allBrandsResult = await getBrands({ page: 1, limit: totalCount }).unwrap();
          setAllBrands(allBrandsResult?.data?.data || []);
        } else {
          // Fallback: use first page data if total count is not available
          setAllBrands(firstPageResult?.data?.data || []);
        }
      } catch (error) {
        console.error("Error fetching brands:", error);
        // Fallback: try with high limit
        try {
          const fallbackResult = await getBrands({ page: 1, limit: 1000 }).unwrap();
          setAllBrands(fallbackResult?.data?.data || []);
        } catch (fallbackError) {
          console.error("Error fetching brands with fallback:", fallbackError);
        }
      }
    };

    fetchAllBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const router = useRouter();
  const [scrollClass, setScrollClass] = useState("");

  const searchParams = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const { data: searchResults, isLoading, isFetching, error } = useSearchAllQuery(
    { search: debouncedSearchTerm },
    { skip: debouncedSearchTerm.length < 3 } // Only search if searchTerm is at least 3 characters long
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const mode = searchParams.get("mode");
  
  // Get current delivery mode (normal or quick)
  const deliveryMode = getDeliveryMode(searchParams);

  // Get token from Redux
  const token = useAppSelector((state) => state.auth.token);

  // Also check token from localStorage as fallback (for immediate updates)
  const [localToken, setLocalToken] = useState<string | null>(null);

  // Get user credentials from storage
  const [userData, setUserData] = useState<any>(null);

  // Check token from localStorage on mount and when it changes
  useEffect(() => {
    const storedToken = getFromStorage(STORAGE_KEYS.token);
    setLocalToken(storedToken);
  }, [token]); // Update when Redux token changes

  // Fetch wishlist when user is logged in - skip on auth pages
  useEffect(() => {
    const fetchWishlist = async () => {
      const currentToken = token || localToken || authToken;
      if (currentToken && !isAuthPage) {
        try {
          const response = await getWishlist({ page: 1, limit: 100 }).unwrap();
          if (response?.data?.data) {
            const productIds = response.data.data.map(
              (item: any) => item.product?.product_id || item.product_id
            );
            dispatch(setWishlistProductIds(productIds));
          }
        } catch (error) {
          // console.error("Error fetching wishlist:", error);
        }
      }
    };

    fetchWishlist();
  }, [token, localToken, authToken, isAuthPage, getWishlist, dispatch]);

  useEffect(() => {
    // Get user data from storage
    const loadUserData = () => {
      const credentialsStr = getFromStorage(STORAGE_KEYS.credentials);
      if (credentialsStr) {
        try {
          const credentials = JSON.parse(credentialsStr);
          setUserData(credentials);
        } catch (error) {
          console.error("Error parsing credentials:", error);
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
    };

    // Load user data on mount and when token changes
    loadUserData();

    // Also listen for custom storage events (for same-tab updates)
    const handleCustomStorageChange = () => {
      loadUserData();
      // Also refresh token from localStorage
      const storedToken = getFromStorage(STORAGE_KEYS.token);
      setLocalToken(storedToken);
    };

    // Listen for storage changes (when user logs in/out or updates profile)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.credentials) {
        loadUserData();
      }
      if (e.key === STORAGE_KEYS.token) {
        setLocalToken(e.newValue);
      }
    };

    // Listen for custom events (for same-tab updates)
    window.addEventListener('credentialsUpdated', handleCustomStorageChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('credentialsUpdated', handleCustomStorageChange);
    };
  }, [token]); // Reload when token changes

  // Separate effect to check for credentials when token exists but userData doesn't
  useEffect(() => {
    const currentToken = token || localToken;
    if (currentToken && !userData) {
      const checkInterval = setInterval(() => {
        const credentialsStr = getFromStorage(STORAGE_KEYS.credentials);
        if (credentialsStr) {
          try {
            const credentials = JSON.parse(credentialsStr);
            setUserData(credentials);
            clearInterval(checkInterval);
          } catch (error) {
            console.error("Error parsing credentials:", error);
          }
        }
      }, 100);

      // Clear interval after 2 seconds to avoid infinite checking
      setTimeout(() => clearInterval(checkInterval), 2000);

      return () => clearInterval(checkInterval);
    }
  }, [token, localToken, userData]);

  // Check if user is logged in (has token from Redux or localStorage)
  const isLoggedIn = !!(token || localToken);

  // Fetch address list when user is logged in - skip on auth pages
  useEffect(() => {
    if (isLoggedIn && !isAuthPage) {
      getAddressList();
    }
  }, [isLoggedIn, isAuthPage, getAddressList]);

  const [isActive, setIsActive] = useState(false);
  const handleClick = () => {
    setIsActive(!isActive);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrollClass("scrolled");
      } else {
        setScrollClass("");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const [selectedAddressId, setSelectedAddressId] = React.useState<string>("");

  // Set the first address as default when addresses load
  React.useEffect(() => {
    if (addresses.length > 0 && !selectedAddressId) {
      setSelectedAddressId(addresses[0].id.toString());
    }
  }, [addresses, selectedAddressId]);

  const handleAddressChange = (event: SelectChangeEvent) => {
    setSelectedAddressId(event.target.value);
  };

  // Get the selected address
  const selectedAddress = addresses.find(
    (addr: Address) => addr.id.toString() === selectedAddressId
  ) || firstAddress;

  // Format display address (pincode + city)
  const formatDeliveryAddress = (addr: Address | null): string => {
    if (!addr) return "Select Address";
    const parts = [addr.pincode, addr.city].filter(Boolean);
    return parts.join(" ") || addr.address?.slice(0, 20) || "Address";
  };

  const showScrolledHeader = pathname !== "/";
  const headerClass = `site_header ${scrollClass} ${showScrolledHeader ? "scrolled_header" : ""
    }`;

  const [value, setValue] = useState(0);

  const [alignment, setAlignment] = React.useState<string | null>("left");
  const handleAlignment = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) => {
    setAlignment(newAlignment);
  };

  const [anchorElCategory, setAnchorElCategory] =
    React.useState<null | HTMLElement>(null);
  const openCategory = Boolean(anchorElCategory);
  const [selectedParentCategory, setSelectedParentCategory] = React.useState<any>(null);

  const handleClickCategory = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElCategory(event.currentTarget);
    // Set first category as default selected when menu opens
    if (categories.length > 0) {
      setSelectedParentCategory(categories[0]);
    }
  };
  const handleCloseCategory = () => {
    setAnchorElCategory(null);
    setSelectedParentCategory(null);
  };

  const handleParentCategoryHover = (category: any) => {
    setSelectedParentCategory(category);
  };

  const [anchorElBrands, setAnchorElBrands] =
    React.useState<null | HTMLElement>(null);
  const openBrands = Boolean(anchorElBrands);
  const handleClickBrands = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElBrands(event.currentTarget);
  };
  const handleCloseBrands = () => {
    setAnchorElBrands(null);
  };

  const [anchorElMenu, setAnchorElMenu] = React.useState<null | HTMLElement>(
    null
  );
  const openMenu = Boolean(anchorElMenu);
  const [selectedNavCategory, setSelectedNavCategory] = React.useState<any>(null);

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>, category: any) => {
    setAnchorElMenu(event.currentTarget);
    setSelectedNavCategory(category);
  };
  const handleCloseMenu = () => {
    setAnchorElMenu(null);
    setSelectedNavCategory(null);
  };

  const fetchProfile = async () => {
    try {
      const res = await getProfile().unwrap();
      if (res?.statusCode == 200) {
        dispatch(setUser({ user: res?.data }));
      }
    } catch (err: any) { }
  };

  useEffect(() => {
    if (token && !isAuthPage) {
      fetchProfile();
    }
  }, [token, isAuthPage]);

  // Fetch cart data - skip on auth pages to avoid 401 errors
  // Use deliveryMode to fetch the correct cart (normal or quick)
  const { data: cartData, refetch: refetchCart } = useGetCartQuery(
    { type: deliveryMode },
    { skip: isAuthPage || !(token || localToken) }
  );
  const cartItems = cartData?.data?.items || [];
  // Calculate total quantity of items in cart (sum of all item quantities)
  const cartTotalQuantity = cartItems.reduce((total: number, item: any) => {
    return total + (Number(item.qty) || 1);
  }, 0);
  const cartTotalAmount = Number(cartData?.data?.sub_total || 0);

  // Refetch cart when login status changes - only if query is enabled (not skipped)
  useEffect(() => {
    // Only refetch if query is not skipped (same condition as skip option)
    // Query is enabled when: NOT on auth page AND user has token
    const shouldRefetch = !isAuthPage && !!(token || localToken);

    if (shouldRefetch && refetchCart) {
      try {
        refetchCart();
      } catch (error) {
        // Silently handle if query hasn't started yet (shouldn't happen with our check)
        console.warn("Cart query refetch failed:", error);
      }
    }
  }, [isLoggedIn, isAuthPage, token, localToken, refetchCart]);

  return (
    <>
      <header className={headerClass}>
        <div className="top_header">
          <div className="container">
            <nav>
              <Box
                component="a"
                className="site_logo"
                onClick={() => router.push("/")}
              >
                <figure>
                  <img src="/images/womanCart_logo.png" alt="Logo" />
                </figure>
              </Box>
              <Box
                component="a"
                className="site_hamburgur"
                onClick={handleClick}
              >
                <MenuIcon />
              </Box>

              <ul className="action_menu">
                <li className="form">
                  <div className="control_group pick_location">
                    <label>Delivery</label>
                    <Select
                      value={selectedAddressId}
                      onChange={handleAddressChange}
                      displayEmpty
                      id="sort_filter"
                      className="sort_menu"
                      input={<OutlinedInput />}
                      inputProps={{
                        "aria-label": "Without label",
                      }}
                      startAdornment={
                        <InputAdornment position="start">
                          <img src="/images/location_icon3.svg" alt="img" />
                        </InputAdornment>
                      }
                      renderValue={() => formatDeliveryAddress(selectedAddress)}
                    >
                      {addresses.length > 0 ? (
                        addresses.map((addr: Address) => (
                          <MenuItem key={addr.id} value={addr.id.toString()}>
                            {addr.pincode} {addr.city}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No addresses found
                        </MenuItem>
                      )}
                    </Select>
                  </div>
                </li>
                <li onClick={() => router.push("/wishlist")} style={{ cursor: "pointer" }}>
                  <Box component="a" className="icon_textBtn">
                    <Badge
                      badgeContent={wishlistProductIds.length}
                      color="secondary"
                      invisible={wishlistProductIds.length === 0}
                      sx={{ marginRight: wishlistProductIds.length === 0 ? "0" : "5px" }}
                    >
                      <FavoriteBorderIcon />
                    </Badge>
                    <p>
                      <span className="d_block">Wishlist</span> My items
                    </p>
                  </Box>
                </li>
                {isLoggedIn ? (
                  <li>
                    <Box
                      component="a"
                      className="icon_textBtn"
                      onClick={() => router.push("/account/profile/")}
                    >
                      {userData?.avatar ? (
                        <img
                          src={userData.avatar}
                          alt="profile"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/images/user_placeholder.svg";
                          }}
                        />
                      ) : (
                        <img src="/images/user_placeholder.svg" alt="icon" />
                      )}
                      <p>
                        <span className="d_block">My Account</span>{" "}
                        {userData?.name ||
                          userData?.email?.split("@")[0] ||
                          "User"}
                      </p>
                    </Box>
                  </li>
                ) : (
                  <li>
                    <Box
                      component="a"
                      className="icon_textBtn"
                      onClick={() => router.push("/auth/login/")}
                    >
                      <img src="/images/user_placeholder.svg" alt="icon" />
                      <p>
                        <span className="d_block">Sign up</span> Account
                      </p>
                    </Box>
                  </li>
                )}
                <li onClick={() => {
                  const cartUrl = deliveryMode === "quick" ? "/cart?mode=quick_delivery" : "/cart";
                  router.push(cartUrl);
                }}>
                  <Badge
                    badgeContent={cartTotalQuantity}
                    color="primary"
                    className="cart_iconBtn"
                    invisible={cartTotalQuantity === 0}
                  >
                    <img src="/images/cart_icon.svg" alt="header" />
                    <p>₹{cartTotalAmount.toFixed(1)}</p>
                  </Badge>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        <div className="mid_header">
          <div className="container">
            {/* Fixed: Properly closing nav element */}
            <nav>
              <ul>
                <li>
                  <Box
                    component="a"
                    id="category-button"
                    aria-controls={openCategory ? "category-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={openCategory ? "true" : undefined}
                    onClick={handleClickCategory}
                  >
                    Categories
                  </Box>
                  <Menu
                    id="category-menu"
                    className="header_menus category_mega_menu"
                    aria-labelledby="category-button"
                    anchorEl={anchorElCategory}
                    open={openCategory}
                    onClose={handleCloseCategory}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          maxWidth: "1200px",
                          width: "100%",
                        },
                      },
                    }}
                  >
                    <Box className="mega_menu_wrapper">
                      {/* Left Sidebar - Parent Categories */}
                      <Box className="mega_menu_sidebar">
                        {categories.length > 0 ? (
                          categories.map((cat: any, index: number) => (
                            <Box
                              key={`header-category-${cat.id || cat.category_id || 'no-id'}-${index}`}
                              className={`parent_category_item ${selectedParentCategory?.id === cat.id ? 'active' : ''}`}
                              onClick={() => handleParentCategoryHover(cat)}
                            >
                              {cat.name || cat.category_name || "Category"}
                            </Box>
                          ))
                        ) : (
                          <Box className="parent_category_item">Loading categories...</Box>
                        )}
                      </Box>

                      {/* Right Content - Children Categories */}
                      <Box className="mega_menu_content">
                        {selectedParentCategory && selectedParentCategory.children && selectedParentCategory.children.length > 0 ? (
                          selectedParentCategory.children.map((subCat: any, subIndex: number) => (
                            <Box key={subCat.id || subIndex} className="subcategory_section">
                              <h4
                                className="subcategory_title"
                                onClick={() => {
                                  router.push(`/product/product-category/?category_id=${subCat.id || subCat.category_id}`);
                                  handleCloseCategory();
                                }}
                              >
                                {subCat.name || subCat.category_name}
                              </h4>
                              {subCat.children && subCat.children.length > 0 && (
                                <Box className="child_categories_grid">
                                  {subCat.children.map((child: any, childIndex: number) => (
                                    <Box
                                      key={`header-child-category-${child.id || child.category_id || 'no-id'}-${subIndex}-${childIndex}`}
                                      className="child_category_item"
                                      onClick={() => {
                                        router.push(`/product/product-category/?categoryIds=${child.id || child.category_id}`);
                                        handleCloseCategory();
                                      }}
                                    >
                                      <figure>
                                        <img
                                          src={child.icon_url || child.image_url || "/images/placeholder.png"}
                                          alt={child.name || child.category_name}
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).src = "/images/placeholder.png";
                                          }}
                                        />
                                      </figure>
                                      <span>{child.name || child.category_name}</span>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          ))
                        ) : selectedParentCategory ? (
                          <Box className="no_children_message">
                            <p>No subcategories available</p>
                            <Box
                              component="a"
                              className="view_all_link"
                              onClick={() => {
                                router.push(`/product/product-category/?categoryIds=${selectedParentCategory.id}`);
                                handleCloseCategory();
                              }}
                            >
                              View all products in {selectedParentCategory.name}
                            </Box>
                          </Box>
                        ) : (
                          <Box className="no_children_message">
                            <p>Click on a category to see subcategories</p>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Menu>
                </li>
                <li>
                  <Box
                    component="a"
                    id="brands-button"
                    aria-controls={openBrands ? "brands-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={openBrands ? "true" : undefined}
                    onClick={handleClickBrands}
                  >
                    Brands
                  </Box>
                  <Menu
                    id="brands-menu"
                    className="header_menus"
                    aria-labelledby="brands-button"
                    anchorEl={anchorElBrands}
                    open={openBrands}
                    onClose={handleCloseBrands}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    slotProps={{
                      paper: {
                        // Filter out invalid props like fullWidth that might be passed to DOM
                        sx: {},
                      },
                    }}
                  >
                    {brandsLoading ? (
                      [
                        <MenuItem key="loading-text" disabled>
                          <span style={{ fontSize: "14px", color: "#4b5563", padding: "8px 0" }}>Loading brands...</span>
                        </MenuItem>,
                        ...Array.from({ length: 8 }).map((_, index) => (
                          <MenuItem key={`brand-skeleton-${index}`} disabled>
                            <figure style={{ margin: 0 }}>
                              <div className="skeleton_shimmer brand_skeleton_logo" />
                            </figure>
                          </MenuItem>
                        ))
                      ]
                    ) : allBrands && allBrands.length > 0 ? (
                      allBrands.map((brand: any, index: number) => (
                        <MenuItem key={`header-brand-${brand.id || brand.brand_id || 'no-id'}-${index}`} onClick={handleCloseBrands}>
                          <figure
                            onClick={() => {
                              if (brand.id) {
                                router.push(
                                  `/product/product-category/?brandIds=${brand.id}`
                                );
                              }
                            }}
                          >
                            <img
                              src={brand.logo_url || "/images/brandLogo_01.png"}
                              alt={brand.name || "Brand"}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/images/brandLogo_01.png";
                              }}
                            />
                          </figure>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No brands available</MenuItem>
                    )}
                  </Menu>
                </li>
                <li>
                  <Box
                    component="a"
                    onClick={() => router.push("/account/blogs/")}
                  >
                    Blogs
                  </Box>
                </li>
              </ul>

              <div className="form" style={{ position: 'relative' }}>
                <div className="control_group search_group">
                  <TextField
                    fullWidth
                    hiddenLabel
                    placeholder="Search in minutes"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    slotProps={{
                      input: {
                        ["start" + "Adornment"]: (
                          <InputAdornment position="start">
                            <img
                              src="/images/form_search_icon.svg"
                              alt="icon"
                            />
                          </InputAdornment>
                        ),
                        // ["end" + "Adornment"]: (
                        //   <InputAdornment position="end">
                        //     <img src="/images/camera2_icon.svg" alt="icon" />
                        //     <img src="/images/microphone_icon.svg" alt="icon" />
                        //   </InputAdornment>
                        // ),
                      },
                    }}
                  />
                </div>
                {debouncedSearchTerm.length >= 3 && (isLoading || isFetching) && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, width: '100%', background: 'white', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxHeight: '300px', overflowY: 'auto', marginTop: '4px' }}>
                    <div style={{ padding: '15px', textAlign: 'center' }}>Loading...</div>
                  </div>
                )}
                {debouncedSearchTerm.length >= 3 && searchResults && searchResults.data && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, width: '100%', background: 'white', border: '1px solid #ccc', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxHeight: '300px', overflowY: 'auto', marginTop: '4px' }}>
                    {/* Products */}
                    {searchResults.data.products && searchResults.data.products.length > 0 && (
                      <div style={{ padding: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>Products</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {searchResults.data.products.map((product: any) => (
                            <li
                              key={product.id || product.product_id}
                              style={{ padding: '8px 5px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}
                              onClick={(e) => {
                                // Don't navigate if clicking the close button
                                if ((e.target as HTMLElement).closest('.close-button')) {
                                  return;
                                }
                                const productId = product.product_id || product.id;
                                if (productId) {
                                  // Navigate to product-category page with productIds parameter
                                  router.push(`/product/product-category/?productIds=${productId}`);
                                  setSearchTerm("");
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <span style={{ flex: 1 }}>{product.name}</span>
                              <IconButton
                                className="close-button"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchTerm("");
                                }}
                                style={{ padding: '4px', marginLeft: '8px' }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Brands */}
                    {searchResults.data.brands && searchResults.data.brands.length > 0 && (
                      <div style={{ padding: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>Brands</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {searchResults.data.brands.map((brand: any) => (
                            <li
                              key={brand.id}
                              style={{ padding: '8px 5px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}
                              onClick={(e) => {
                                // Don't navigate if clicking the close button
                                if ((e.target as HTMLElement).closest('.close-button')) {
                                  return;
                                }
                                const brandId = brand.id || brand.brand_id;
                                if (brandId) {
                                  // Navigate to product-category page with brandIds parameter
                                  router.push(`/product/product-category/?brandIds=${brandId}`);
                                  setSearchTerm("");
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <span style={{ flex: 1 }}>{brand.name}</span>
                              <IconButton
                                className="close-button"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchTerm("");
                                }}
                                style={{ padding: '4px', marginLeft: '8px' }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Categories */}
                    {searchResults.data.categories && searchResults.data.categories.length > 0 && (
                      <div style={{ padding: '10px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>Categories</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {searchResults.data.categories.map((category: any) => (
                            <li
                              key={category.id || category.category_id}
                              style={{ padding: '8px 5px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}
                              onClick={(e) => {
                                // Don't navigate if clicking the close button
                                if ((e.target as HTMLElement).closest('.close-button')) {
                                  return;
                                }
                                const categoryId = category.category_id || category.id;
                                if (categoryId) {
                                  // Navigate to product-category page with categoryIds parameter
                                  router.push(`/product/product-category/?categoryIds=${categoryId}`);
                                  setSearchTerm("");
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <span style={{ flex: 1 }}>{category.name}</span>
                              <IconButton
                                className="close-button"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchTerm("");
                                }}
                                style={{ padding: '4px', marginLeft: '8px' }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(!searchResults.data.products?.length &&
                      !searchResults.data.brands?.length &&
                      !searchResults.data.categories?.length) && (
                        <div style={{ padding: '15px', textAlign: 'center' }}>
                          <p style={{ margin: 0 }}>No results found for "{searchTerm}".</p>
                        </div>
                      )}
                  </div>
                )}
              </div>

              <ToggleButtonGroup
                value={alignment}
                exclusive
                onChange={handleAlignment}
                aria-label="text alignment"
              >
                <ToggleButton
                  value="left"
                  aria-label="left aligned"
                  onClick={() => router.push("/")}
                >
                  <img src="/images/womencart_icon.png" alt="icon" /> WomenCart
                </ToggleButton>
                <ToggleButton
                  value="center"
                  aria-label="centered"
                  onClick={() => {
                    router.push("/?mode=quick_delivery");
                  }}
                >
                  <img src="/images/quickDelivery_icon.svg" alt="icon" /> Quick
                  Delivery
                </ToggleButton>
              </ToggleButtonGroup>
            </nav>
          </div>
        </div>
        <div className="nav_header">
          <div className="container">
            <nav>
              <Tabs
                value={value}
                onChange={(_e, newValue) => setValue(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="scrollable tabs"
                className="nav_categories"
              >
                {categories.length > 0 && categories.map((cat: any, index: number) => (
                  <Tab
                    key={`header-nav-category-${cat.id || cat.category_id || 'no-id'}-${index}`}
                    label={cat.name || cat.category_name || "Category"}
                    id={`navMenu-button-${cat.id || index}`}
                    aria-controls={openMenu && selectedNavCategory?.id === cat.id ? "navMenu-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={openMenu && selectedNavCategory?.id === cat.id ? "true" : undefined}
                    onClick={(e) => {
                      if (cat.children && cat.children.length > 0) {
                        handleClickMenu(e, cat);
                      } else {
                        router.push(`/product/product-category/?category_id=${cat.id || cat.category_id}`);
                      }
                    }}
                  />
                ))}
                <Tab
                  label="Men Store"
                  onClick={() => router.push("/product/product-category/")}
                />
                <Tab
                  label="New Arrivals"
                  onClick={() => router.push("/product/product-category/")}
                />
                <Tab
                  label="Best Sellers"
                  onClick={() => router.push("/product/product-category/")}
                />
                <Tab
                  label="Flash Sales"
                  onClick={() => router.push("/product/product-category/")}
                />
              </Tabs>
              {/* Mega Menu for nav category with children */}
              <Menu
                id="navMenu-menu"
                className="header_menus category_mega_menu nav_category_menu"
                aria-labelledby={`navMenu-button-${selectedNavCategory?.id}`}
                anchorEl={anchorElMenu}
                open={openMenu}
                onClose={handleCloseMenu}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      maxWidth: "1000px",
                      width: "100%",
                    },
                  },
                }}
              >
                <Box className="mega_menu_wrapper nav_mega_wrapper">
                  {/* Left Sidebar - Subcategories */}
                  <Box className="mega_menu_sidebar">
                    {selectedNavCategory?.children && selectedNavCategory.children.length > 0 ? (
                      selectedNavCategory.children.map((subCat: any, index: number) => (
                        <Box
                          key={`header-nav-subcategory-${subCat.id || subCat.category_id || 'no-id'}-${index}`}
                          className="parent_category_item"
                          onClick={() => {
                            router.push(`/product/product-category/?category_id=${subCat.id || subCat.category_id}`);
                            handleCloseMenu();
                          }}
                        >
                          {subCat.name || subCat.category_name}
                        </Box>
                      ))
                    ) : (
                      <Box className="parent_category_item">No subcategories</Box>
                    )}
                  </Box>

                  {/* Right Content - Children of subcategories */}
                  <Box className="mega_menu_content">
                    {selectedNavCategory?.children && selectedNavCategory.children.length > 0 ? (
                      selectedNavCategory.children.map((subCat: any, subIndex: number) => (
                        <Box key={`header-nav-subcategory-section-${subCat.id || subCat.category_id || 'no-id'}-${subIndex}`} className="subcategory_section">
                          <h4
                            className="subcategory_title"
                            onClick={() => {
                              router.push(`/product/product-category/?category_id=${subCat.id || subCat.category_id}`);
                              handleCloseMenu();
                            }}
                          >
                            {subCat.name || subCat.category_name}
                          </h4>
                          {subCat.children && subCat.children.length > 0 && (
                            <Box className="child_categories_grid">
                              {subCat.children.map((child: any, childIndex: number) => (
                                <Box
                                  key={`header-nav-child-category-${child.id || child.category_id || 'no-id'}-${subIndex}-${childIndex}`}
                                  className="child_category_item"
                                  onClick={() => {
                                    router.push(`/product/product-category/?category_id=${child.id || child.category_id}`);
                                    handleCloseMenu();
                                  }}
                                >
                                  <figure>
                                    <img
                                      src={child.icon_url || child.image_url || "/images/placeholder.png"}
                                      alt={child.name || child.category_name}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = "/images/placeholder.png";
                                      }}
                                    />
                                  </figure>
                                  <span>{child.name || child.category_name}</span>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))
                    ) : (
                      <Box className="no_children_message">
                        <p>No subcategories available</p>
                        {selectedNavCategory && (
                          <Box
                            component="a"
                            className="view_all_link"
                            onClick={() => {
                              router.push(`/product/product-category/?category_id=${selectedNavCategory.id}`);
                              handleCloseMenu();
                            }}
                          >
                            View all products in {selectedNavCategory.name}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Menu>
              <Box component="a" className="text_btn">
                Refer Friend, Get $20
              </Box>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
